import { memo, type ReactElement } from 'react';

import { PrivateWorkspaceAgentAssetListPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentAssetListPillChip';
import { PrivateWorkspaceAgentAssetPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentAssetPillChip';
import { PrivateWorkspaceAgentBoardListPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentBoardListPillChip';
import { PrivateWorkspaceAgentBoardPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentBoardPillChip';
import { PrivateWorkspaceAgentChangePreviewPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentChangePreviewPillChip';
import { PrivateWorkspaceAgentCommentPillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentCommentPillChip';
import { PrivateWorkspaceAgentContextSourceFieldValuePillChip } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentContextSourceFieldValuePillChip';
import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { type WorkspaceAgentReferenceMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';

export type PrivateWorkspaceAgentPanelReferencePillChipProps = {
  /** Mid-stream, a moniker with no pill yet is not a broken reference. */
  isStreaming: boolean;
  pill: WorkspaceAgentReferenceMessagePill | undefined;
  rawId: string;
};

/** No wire pill yet, or a type this module does not render (e.g. `table`). */
const unresolvedWorkspaceAgentReferencePillChip = (isStreaming: boolean, rawId: string): ReactElement =>
  isStreaming ? <WorkspaceAgentPendingPill /> : <WorkspaceAgentUnavailablePill kind="reference" referenceId={rawId} />;

/** Wire `metadata.pills` reference types. Table artifact chips stay in `PrivateWorkspaceAgentPanelMessagePillChip`. */
export const PrivateWorkspaceAgentPanelReferencePillChip = memo(
  ({ isStreaming, pill, rawId }: PrivateWorkspaceAgentPanelReferencePillChipProps) => {
    if (!pill) {
      return unresolvedWorkspaceAgentReferencePillChip(isStreaming, rawId);
    }

    switch (pill.type) {
      case 'comment':
        return <PrivateWorkspaceAgentCommentPillChip pill={pill} />;
      case 'asset':
        return <PrivateWorkspaceAgentAssetPillChip isStreaming={isStreaming} pill={pill} />;
      case 'assetList':
        return <PrivateWorkspaceAgentAssetListPillChip isStreaming={isStreaming} pill={pill} />;
      case 'board':
        return <PrivateWorkspaceAgentBoardPillChip pill={pill} />;
      case 'boardList':
        return <PrivateWorkspaceAgentBoardListPillChip isStreaming={isStreaming} pill={pill} />;
      case 'changePreview':
        return <PrivateWorkspaceAgentChangePreviewPillChip isStreaming={isStreaming} pill={pill} />;
      case 'contextSourceFieldValue':
        return <PrivateWorkspaceAgentContextSourceFieldValuePillChip pill={pill} />;
      default:
        return unresolvedWorkspaceAgentReferencePillChip(isStreaming, rawId);
    }
  },
);

PrivateWorkspaceAgentPanelReferencePillChip.displayName = 'PrivateWorkspaceAgentPanelReferencePillChip';
